import { useAirQualityData, SensorType } from 'common';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { YStack, XStack, Text } from 'tamagui';
import { tokens } from 'tamagui-config/src/tokens';

import { LoadingOverlay } from '../../../components/common';
import { HubLineChart } from '../../../components/common/HubLineChart';
import { useAppSync } from '../../../contexts/AppSyncContext';

export const InternalHumidityContent: React.FC = () => {
  const { t } = useTranslation();
  const { sensors, selectedHubId } = useAppSync();

  // Find the air quality sensor for the selected hub
  const airQualitySensor = sensors.find(
    (sensor) => sensor.hubId === selectedHubId && sensor.type === SensorType.AIR_QUALITY,
  );

  const { data, loading, error, latestData } = useAirQualityData({
    sensorId: airQualitySensor?.id,
    days: 7, // Last 7 days of data
    enabled: !!airQualitySensor?.id,
  });

  // Transform the data to format required by HubLineChart, grouping by day of week
  const humidityData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    // Group humidity data by day of week
    const dailyHumidity = data.reduce(
      (acc, day) => {
        const date = new Date(day.label);
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });

        if (!acc[dayOfWeek]) {
          acc[dayOfWeek] = { sum: 0, count: 0 };
        }

        // Add humidity to the accumulator
        acc[dayOfWeek].sum += day.humidity;
        acc[dayOfWeek].count += 1;

        return acc;
      },
      {} as Record<string, { sum: number; count: number }>,
    );

    // Show last 7 days ending with today
    const chartData: Array<{ value: number; label: string; timestamp: number }> = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayLabel = date.toLocaleDateString('en-US', { weekday: 'short' });

      const dayData = dailyHumidity[dayLabel];

      if (dayData) {
        chartData.push({
          value: Math.round(dayData.sum / dayData.count),
          label: dayLabel,
          timestamp: date.getTime(),
        });
      } else {
        chartData.push({
          value: 0,
          label: dayLabel,
          timestamp: date.getTime(),
        });
      }
    }

    return chartData;
  }, [data]);

  if (loading) {
    return <LoadingOverlay isVisible={true} />;
  }

  if (error || !humidityData.length) {
    return (
      <XStack flex={1} justifyContent="center" alignItems="center">
        <Text fontSize="$6" color={tokens.color.textLight} textAlign="center">
          {error || t('air.noHumidityData')}
        </Text>
      </XStack>
    );
  }

    const mostRecentValue =
      latestData ? Math.round(latestData.humidity) : null;

  return (
    <YStack>
      <Text fontFamily="$heading" fontWeight="$9.5" fontSize="$9.5" color={tokens.color.white12}>
        {`${mostRecentValue || '--'}%`}
      </Text>

      <HubLineChart data={humidityData} unit={'%'} />
    </YStack>
  );
};
